/**
 * The theme rule, and the only place its storage key is spelled (TA-343,
 * from TA-252's Not done list; CLAUDE.md 12.1: two themes, light and dark,
 * switched by `data-theme` on <html>).
 *
 * Three files used to hold the same key and the same rule: the inline boot
 * script in `app/layout.tsx`, a `useLayoutEffect` inside `Sidebar.tsx`, and
 * `ThemeSwitch.tsx`. Three copies is three chances to change two of them, and
 * the sidebar copy was also in the wrong place: it was the pre-paint fallback
 * for the one document the boot script never runs on (Next's client-rendered
 * `__next_error__`), so a page rendered outside `(operator)` had no fallback
 * at all. `ThemeBeforePaint` in the root layout is that fallback now, and it
 * reads the rule from here.
 *
 * THE BOOT SCRIPT IS A MIRROR, HELD TO THE ORIGINAL BY A TEST. It is a string
 * that runs in <head> before any bundle exists, so it cannot import
 * `resolveTheme`; it can only restate it. `theme.test.ts` runs the exact
 * string this module exports in `node:vm` against stub globals and asserts
 * the attribute it sets equals `resolveTheme`'s answer for the same inputs,
 * so the two cannot drift apart silently.
 */

export type Theme = "light" | "dark";

/** Where a chosen theme is remembered, per browser. */
export const THEME_STORAGE_KEY = "ackra-theme";

/** The one question anything here asks the OS. */
export const THEME_QUERY = "(prefers-color-scheme: dark)";

/**
 * The saved choice, else the OS.
 *
 * Anything that is not one of the two themes counts as nothing saved: a stale
 * value or a hand-edited entry falls back to the OS rather than pinning
 * light, which would read as a choice the operator never made.
 */
export function resolveTheme(saved: string | null | undefined, prefersDark: boolean): Theme {
  if (saved === "light" || saved === "dark") return saved;
  return prefersDark ? "dark" : "light";
}

/**
 * The same rule, as the inline script the document ships.
 *
 * Kept to what it needs: one storage read, one media query asked only when
 * the saved value does not decide, one attribute. Storage that throws leaves
 * the attribute unset and `ThemeBeforePaint` decides a frame later; that is
 * the behaviour this script had before TA-343 moved it here, and TA-343 is a
 * refactor, not a change of what the browser does.
 */
export const THEME_BOOT_SCRIPT =
  `(function(){try{var t=localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)});` +
  `if(t!=="light"&&t!=="dark"){t=matchMedia(${JSON.stringify(THEME_QUERY)}).matches?"dark":"light"}` +
  `document.documentElement.dataset.theme=t}catch(e){}})()`;
